import React, { useEffect, useMemo } from "react"
import { AntdSelect } from "components"
import { useAppDispatch, useAppSelector } from "hooks"
import { getUniquePosts, isUniquePostsLoading, selectUniquePosts } from "store/Location/locationPosts.slice"
import {
	ALL_FILTER_VALUE,
	SHIFT_REFERENCE_OPTIONS,
	TRAINING_TRACKER_FIELDS,
	TrainingTrackerSiteOption,
	TrainingTrackerEmployeeOption,
	TrainingTrackerBranchOption,
	isAllFilterValue,
	isShiftReferenceDisabled,
	selectedPostStillAvailable,
} from "./training-tracker.utils"

interface Props {
	formData: Record<string, any>
	onFieldChange: (name: string, value: any) => void
	employees: TrainingTrackerEmployeeOption[]
	branches: TrainingTrackerBranchOption[]
	sites: TrainingTrackerSiteOption[]
}

const TrainingTrackerFilters: React.FC<Props> = ({ formData, onFieldChange, employees, branches, sites }) => {
	const dispatch = useAppDispatch()
	const posts = useAppSelector(selectUniquePosts())
	const postsLoading = useAppSelector(isUniquePostsLoading())
	const selectedBranch = formData[TRAINING_TRACKER_FIELDS.BRANCH]
	const selectedSite = formData[TRAINING_TRACKER_FIELDS.SITE]
	const selectedPostName = formData[TRAINING_TRACKER_FIELDS.POST_NAME]
	const shiftReferenceDisabled = isShiftReferenceDisabled(selectedPostName)
	const isBranchSelected = !isAllFilterValue(selectedBranch)

	useEffect(() => {
		dispatch(getUniquePosts(selectedSite))
	}, [dispatch, selectedSite])

	useEffect(() => {
		if (!posts.length) return
		if (!selectedPostStillAvailable(selectedPostName, posts)) {
			onFieldChange(TRAINING_TRACKER_FIELDS.POST_NAME, ALL_FILTER_VALUE)
		}
	}, [posts, selectedPostName, onFieldChange])

	const branchOptions = useMemo(
		() => [
			{ label: "Select Branch", value: ALL_FILTER_VALUE },
			...branches.map(({ _id, name }) => ({
				label: name,
				value: _id,
			})),
		],
		[branches],
	)

	const employeeOptions = useMemo(() => {
		if (!isBranchSelected) return []
		return [
			{ label: "All Employees", value: ALL_FILTER_VALUE },
			...employees
				.filter(employee => employee.branch === selectedBranch)
				.map(({ _id, firstName, lastName }) => ({
					label: `${firstName} ${lastName}`,
					value: _id,
				})),
		]
	}, [employees, isBranchSelected, selectedBranch])

	const siteOptions = useMemo(
		() => [
			{ label: "All Sites", value: ALL_FILTER_VALUE },
			...sites.map(({ _id, name }) => ({
				label: name,
				value: _id,
			})),
		],
		[sites],
	)

	const postOptions = useMemo(
		() => [
			{ label: "All Posts", value: ALL_FILTER_VALUE },
			...posts.map(({ name }) => ({
				label: name,
				value: name,
			})),
		],
		[posts],
	)

	return (
		<div className="flex flex-wrap gap-8">
			<AntdSelect
				isControlled
				onChange={value => onFieldChange(TRAINING_TRACKER_FIELDS.BRANCH, value)}
				selected={formData[TRAINING_TRACKER_FIELDS.BRANCH] ?? ALL_FILTER_VALUE}
				label="Branch"
				name={TRAINING_TRACKER_FIELDS.BRANCH}
				containerClass="w-84 h-10! space-y-0"
				size="middle"
				options={branchOptions}
				maxTagLength={28}
			/>
			<AntdSelect
				isControlled
				disabled={!isBranchSelected}
				onChange={value => onFieldChange(TRAINING_TRACKER_FIELDS.EMPLOYEE, value)}
				selected={isBranchSelected ? formData[TRAINING_TRACKER_FIELDS.EMPLOYEE] ?? ALL_FILTER_VALUE : undefined}
				label="Employee"
				name={TRAINING_TRACKER_FIELDS.EMPLOYEE}
				containerClass="w-84 h-10! space-y-0"
				size="middle"
				options={employeeOptions}
				maxTagLength={28}
				placeholder={isBranchSelected ? "Search" : "Select Branch first"}
			/>
			<AntdSelect
				isControlled
				onChange={value => onFieldChange(TRAINING_TRACKER_FIELDS.SITE, value)}
				selected={formData[TRAINING_TRACKER_FIELDS.SITE] ?? ALL_FILTER_VALUE}
				label="Site"
				name={TRAINING_TRACKER_FIELDS.SITE}
				containerClass="w-84 h-10! space-y-0"
				size="middle"
				options={siteOptions}
				maxTagLength={28}
			/>
			<AntdSelect
				isControlled
				onChange={value => onFieldChange(TRAINING_TRACKER_FIELDS.POST_NAME, value)}
				selected={postsLoading ? undefined : formData[TRAINING_TRACKER_FIELDS.POST_NAME] ?? ALL_FILTER_VALUE}
				label="Post"
				name={TRAINING_TRACKER_FIELDS.POST_NAME}
				containerClass="w-84 h-10! space-y-0"
				size="middle"
				options={postsLoading ? [] : postOptions}
				maxTagLength={28}
				loading={postsLoading}
				disabled={postsLoading}
				placeholder={postsLoading ? "Loading..." : "Search"}
			/>
			<AntdSelect
				isControlled
				disabled={shiftReferenceDisabled}
				onChange={value => onFieldChange(TRAINING_TRACKER_FIELDS.SHIFT_REFERENCE, value)}
				selected={formData[TRAINING_TRACKER_FIELDS.SHIFT_REFERENCE]}
				label="Shift Reference"
				name={TRAINING_TRACKER_FIELDS.SHIFT_REFERENCE}
				containerClass="w-84 h-10! space-y-0"
				size="middle"
				options={SHIFT_REFERENCE_OPTIONS}
				maxTagLength={28}
			/>
		</div>
	)
}

export default TrainingTrackerFilters
